// Settings → Fleet and CI → Alerts (P-004 milestone 3, docs/alerts.md), owners only: what the
// workspace alerts on (runner offline past a threshold, CI failing on a branch), the signed-in
// owner's own channels (web push on this device, email, Telegram), each with "Send a test", and
// the recent incidents. One message when an incident opens and one when it closes. Channel keys
// stay in board.env: a channel the server has no settings for says so and is not hidden.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  api,
  errorMessage,
  type AlertChannel,
  type AlertChannels,
  type AlertIncident,
  type AlertSettings,
  type AlertState,
} from '../api/client';
import { Icon } from '../components/Icon';
import { Badge, Button, Input, Switch, cn, mono } from '../components/ui';
import { currentEndpoint, pushSupport, PushRefused, subscribe, unsubscribe, type PushSupport } from '../lib/push';
import { ago } from '../lib/time';
import { useLoad } from '../lib/useLoad';
import { CopyField, H2, H3, SECTION, SMALL, SUB, Status, type Outcome } from './FleetSettings';

const ROW = 'grid gap-2 border-t border-hairline py-3.5 first:border-t-0';
const NAME = 'm-0 text-[13px] font-semibold text-ink-display';

function NotConfigured() {
  return <Badge tone="muted">Not configured on this server</Badge>;
}

// ------------------------------------------------------------------ what to alert on

function Kinds({ ws, settings, onSaved }: { ws: number; settings: AlertSettings; onSaved: (s: AlertSettings) => void }) {
  const [minutes, setMinutes] = useState(String(settings.offline_minutes));
  const [outcome, setOutcome] = useState<Outcome>(null);
  useEffect(() => setMinutes(String(settings.offline_minutes)), [settings.offline_minutes]);
  async function save(change: Partial<AlertSettings>, said: string) {
    setOutcome(null);
    const { data, error, response } = await api.PUT('/api/workspaces/{ws_id}/alerts/settings', {
      params: { path: { ws_id: ws } },
      body: change,
    });
    if (data) {
      onSaved(data.settings);
      setOutcome({ ok: true, text: said });
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  function saveMinutes(e: FormEvent) {
    e.preventDefault();
    const n = Number(minutes);
    if (!Number.isInteger(n) || n < 1 || n > 1440) {
      setOutcome({ ok: false, text: 'The threshold is a whole number of minutes, 1 to 1440.' });
      return;
    }
    void save({ offline_minutes: n }, `Runner alerts after ${n} minutes offline.`);
  }
  const changed = minutes !== String(settings.offline_minutes);
  return (
    <>
      <h3 className={H3}>What to alert on</h3>
      <p className={SUB}>For every owner of this workspace. Problems already open when a kind is switched on are recorded, not sent.</p>
      <div className="rounded-lg border border-hairline px-3.5" role="group" aria-label="Alert kinds">
        <div className={ROW}>
          <div className="flex items-start justify-between gap-3">
            <span className="min-w-0">
              <b className={NAME}>Runner offline</b>
              <span className={SMALL}>A self-hosted runner GitHub reports offline for longer than the threshold, and when it is back.</span>
            </span>
            <Switch
              checked={settings.runner_offline}
              aria-label="Alert when a runner is offline"
              onChange={(v) => void save({ runner_offline: v }, v ? 'Runner alerts on.' : 'Runner alerts off; open ones closed without a message.')}
            />
          </div>
          <form className="flex flex-wrap items-center gap-2 text-[12.5px]" onSubmit={saveMinutes}>
            <label htmlFor="offline-minutes" className="text-ink-muted">
              Offline for more than
            </label>
            <span className="w-[76px] flex-none">
              <Input
                id="offline-minutes"
                type="number"
                inputMode="numeric"
                min={1}
                max={1440}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </span>
            <span className="text-ink-muted">minutes</span>
            {changed && (
              <Button type="submit" size="sm" variant="primary">
                Save
              </Button>
            )}
          </form>
        </div>
        <div className={ROW}>
          <div className="flex items-start justify-between gap-3">
            <span className="min-w-0">
              <b className={NAME}>CI failing</b>
              <span className={SMALL}>
                The first failed run of a workflow on a branch, and when it passes again. Cancelled, skipped and superseded runs change
                nothing.
              </span>
            </span>
            <Switch
              checked={settings.ci_failure}
              aria-label="Alert when CI fails"
              onChange={(v) => void save({ ci_failure: v }, v ? 'CI alerts on.' : 'CI alerts off; open ones closed without a message.')}
            />
          </div>
        </div>
      </div>
      <Status outcome={outcome} />
    </>
  );
}

// ------------------------------------------------------------------ channels

function TestButton({ ws, channel, label, disabled }: { ws: number; channel: AlertChannel; label: string; disabled?: boolean }) {
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [busy, setBusy] = useState(false);
  async function test() {
    setBusy(true);
    setOutcome(null);
    const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/alerts/test', {
      params: { path: { ws_id: ws } },
      body: { channel },
    });
    setBusy(false);
    if (!data) setOutcome({ ok: false, text: errorMessage(error, response.status) });
    else if (data.ok) setOutcome({ ok: true, text: channel === 'push' ? `Sent to ${data.sent} device${data.sent === 1 ? '' : 's'}.` : 'Sent.' });
    else setOutcome({ ok: false, text: data.error ?? 'Not sent.' });
  }
  return (
    <span className="grid justify-items-end gap-1">
      <Button size="sm" onClick={() => void test()} disabled={busy || disabled} aria-label={`Send a test ${label}`}>
        Send a test
      </Button>
      {outcome && (
        <span className={cn('text-right text-[11.5px]', outcome.ok ? 'text-checks-pass-ink' : 'font-medium text-checks-fail-ink')} role={outcome.ok ? 'status' : 'alert'}>
          {outcome.text}
        </span>
      )}
    </span>
  );
}

function Channel({ name, state, children, test }: { name: string; state: ReactNode; children: ReactNode; test?: ReactNode }) {
  return (
    <li className={ROW} data-channel={name}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <b className={NAME}>{name}</b>
          {state}
        </span>
        {test}
      </div>
      {children}
    </li>
  );
}

function LastError({ error, sent }: { error: string | null; sent: string | null }) {
  if (error)
    return (
      <span className="flex items-center gap-1.5 text-[11.5px] font-medium text-checks-fail-ink">
        <Icon name="warn" small />
        Last send failed: {error}
      </span>
    );
  return sent ? <span className={SMALL}>Last sent {ago(sent)}</span> : null;
}

const SUPPORT_TEXT: Record<Exclude<PushSupport, 'ok'>, string> = {
  unsupported: 'This browser cannot receive push notifications. On an iPhone or iPad, add Shipyard to the Home Screen and open it from there.',
  'no-worker': 'The app’s service worker is not running in this window, so it cannot subscribe here.',
  denied: 'Notifications are blocked for this site; allow them in the browser’s settings first.',
};

function PushChannel({ ws, channels, onChanged }: { ws: number; channels: AlertChannels; onChanged: (c: AlertChannels) => void }) {
  const push = channels.push;
  const [support, setSupport] = useState<PushSupport | null>(null);
  const [mine, setMine] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    void pushSupport().then((s) => live && setSupport(s));
    void currentEndpoint().then((e) => live && setMine(e), () => undefined);
    return () => {
      live = false;
    };
  }, []);
  const here = push.devices.find((d) => d.endpoint === mine);
  async function enable() {
    if (!push.public_key) return;
    setBusy(true);
    setOutcome(null);
    try {
      const sub = await subscribe(push.public_key);
      const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/alerts/push', {
        params: { path: { ws_id: ws } },
        body: sub,
      });
      if (data) {
        setMine(sub.endpoint);
        onChanged(data.channels);
        setOutcome({ ok: true, text: 'This device will get alerts.' });
      } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
    } catch (e) {
      setOutcome({ ok: false, text: e instanceof PushRefused ? e.message : 'The browser refused to subscribe.' });
    }
    setBusy(false);
  }
  async function remove(id: number, thisDevice: boolean) {
    setOutcome(null);
    if (thisDevice) await unsubscribe().catch(() => undefined);
    const { error, response } = await api.DELETE('/api/workspaces/{ws_id}/alerts/push/{sub_id}', {
      params: { path: { ws_id: ws, sub_id: id } },
    });
    if (response.ok || response.status === 404) {
      if (thisDevice) setMine(null);
      onChanged({ ...channels, push: { ...push, devices: push.devices.filter((d) => d.id !== id) } });
      setOutcome({ ok: true, text: thisDevice ? 'This device will no longer get alerts.' : 'Removed that device.' });
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  const n = push.devices.length;
  return (
    <Channel
      name="Web push"
      state={!push.configured ? <NotConfigured /> : n ? <Badge tone="success">On for {n} device{n === 1 ? '' : 's'}</Badge> : <Badge>Off</Badge>}
      test={push.configured && <TestButton ws={ws} channel="push" label="push notification" disabled={n === 0} />}
    >
      <span className={SMALL}>A notification on this phone or computer, from the installed app or this site. One per device.</span>
      {push.configured && (
        <div className="flex flex-wrap items-center gap-2">
          {here ? (
            <Button size="sm" onClick={() => void remove(here.id, true)}>
              Disable on this device
            </Button>
          ) : (
            <Button size="sm" variant="primary" onClick={() => void enable()} disabled={busy || support !== 'ok'}>
              <Icon name="plus" small />
              Enable on this device
            </Button>
          )}
          {support && support !== 'ok' && !here && <span className="text-[12px] text-ink-muted">{SUPPORT_TEXT[support]}</span>}
        </div>
      )}
      {n > 0 && (
        <ul className="m-0 grid list-none gap-1 p-0" aria-label="Devices">
          {push.devices.map((d) => (
            <li key={d.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 text-[12px]">
              <span className="min-w-0">
                <span className={mono}>{d.service}</span>
                {d.endpoint === mine && <b className="font-semibold text-ink-display"> · this device</b>}
                <span className="text-ink-muted"> · since {ago(d.created_at)}</span>
                {d.error && <span className="block font-medium text-checks-fail-ink">Last send failed: {d.error}</span>}
              </span>
              {d.endpoint !== mine && (
                <Button size="sm" variant="ghost" onClick={() => void remove(d.id, false)} aria-label={`Remove the device on ${d.service}`}>
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Status outcome={outcome} />
    </Channel>
  );
}

async function putChannels(ws: number, body: { email?: { on?: boolean; address?: string | null }; telegram?: { on?: boolean; chat_id?: string | null } }) {
  return api.PUT('/api/workspaces/{ws_id}/alerts/channels', { params: { path: { ws_id: ws } }, body });
}

function EmailChannel({ ws, email, onChanged }: { ws: number; email: AlertChannels['email']; onChanged: (c: AlertChannels) => void }) {
  const [address, setAddress] = useState(email.address ?? '');
  const [outcome, setOutcome] = useState<Outcome>(null);
  useEffect(() => setAddress(email.address ?? ''), [email.address]);
  async function save(body: { on?: boolean; address?: string | null }, said: string) {
    setOutcome(null);
    const { data, error, response } = await putChannels(ws, { email: body });
    if (data) {
      onChanged(data.channels);
      setOutcome({ ok: true, text: said });
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  const to = email.address ?? email.default_address;
  const changed = address.trim() !== (email.address ?? '');
  return (
    <Channel
      name="Email"
      state={!email.configured ? <NotConfigured /> : email.on ? <Badge tone="success">On</Badge> : <Badge>Off</Badge>}
      test={email.configured && <TestButton ws={ws} channel="email" label="email" />}
    >
      <span className={SMALL}>
        {to ? (
          <>
            To <span className={mono}>{to}</span>
            {email.address ? '' : ', from your terica sign-in'}.
          </>
        ) : (
          'Type the address to send to.'
        )}
      </span>
      {email.configured && (
        <div className="flex flex-wrap items-center gap-2">
          <Switch
            checked={email.on}
            aria-label="Email me alerts"
            onChange={(v) => void save({ on: v }, v ? 'Email alerts on.' : 'Email alerts off.')}
          />
          <form
            className="flex min-w-0 flex-1 flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void save({ address: address.trim() || null }, address.trim() ? `Alerts go to ${address.trim()}.` : 'Alerts go to your sign-in address.');
            }}
          >
            <label htmlFor="alert-email" className="sr-only">
              Email address for alerts
            </label>
            <Input
              id="alert-email"
              sans
              type="email"
              className="min-w-[180px] max-w-[300px] flex-1 phone:min-w-0 phone:max-w-none phone:basis-full"
              placeholder={email.default_address ?? 'you@example.com'}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            {changed && (
              <Button type="submit" size="sm" variant="primary">
                Save address
              </Button>
            )}
          </form>
        </div>
      )}
      <LastError error={email.error} sent={email.sent_at} />
      <Status outcome={outcome} />
    </Channel>
  );
}

function TelegramChannel({
  ws,
  telegram,
  onChanged,
  reload,
}: {
  ws: number;
  telegram: AlertChannels['telegram'];
  onChanged: (c: AlertChannels) => void;
  reload: () => void;
}) {
  const [link, setLink] = useState<string | null>(null);
  const [chat, setChat] = useState('');
  const [outcome, setOutcome] = useState<Outcome>(null);
  const waiting = Boolean(telegram.code) && !telegram.linked;
  // While a code is out, look again every 3 s: the worker links the chat when the bot hears it.
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(reload, 3000);
    return () => clearInterval(t);
  }, [waiting, reload]);
  async function code() {
    setOutcome(null);
    const { data, error, response } = await api.POST('/api/workspaces/{ws_id}/alerts/telegram/code', { params: { path: { ws_id: ws } } });
    if (data) {
      setLink(data.link);
      reload();
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  async function save(body: { on?: boolean; chat_id?: string | null }, said: string) {
    setOutcome(null);
    const { data, error, response } = await putChannels(ws, { telegram: body });
    if (data) {
      onChanged(data.channels);
      setOutcome({ ok: true, text: said });
      setChat('');
    } else setOutcome({ ok: false, text: errorMessage(error, response.status) });
  }
  const bot = telegram.bot_username;
  const href = link ?? (bot && telegram.code ? `https://t.me/${bot}?start=${telegram.code}` : null);
  return (
    <Channel
      name="Telegram"
      state={
        !telegram.configured ? (
          <NotConfigured />
        ) : telegram.linked ? (
          <Badge tone={telegram.on ? 'success' : 'muted'}>{telegram.on ? 'On' : 'Off'}</Badge>
        ) : (
          <Badge>Not linked</Badge>
        )
      }
      test={telegram.configured && telegram.linked && <TestButton ws={ws} channel="telegram" label="Telegram message" />}
    >
      <span className={SMALL}>
        A message from this server’s bot{bot ? (
          <>
            , <span className={mono}>@{bot}</span>
          </>
        ) : null}
        , in your own chat.
      </span>
      {telegram.configured && telegram.linked && (
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <Switch checked={telegram.on} aria-label="Telegram me alerts" onChange={(v) => void save({ on: v }, v ? 'Telegram alerts on.' : 'Telegram alerts off.')} />
          <span className="text-ink-muted">
            Linked to chat <span className={mono}>{telegram.chat_hint}</span>
          </span>
          <Button size="sm" variant="ghost" onClick={() => void save({ chat_id: null }, 'Unlinked.')}>
            Unlink
          </Button>
        </div>
      )}
      {telegram.configured && !telegram.linked && (
        <div className="grid gap-2.5">
          {waiting && telegram.code ? (
            <div className="grid gap-2 rounded-lg border border-hairline-strong bg-surface p-3" role="status" aria-label="Link Telegram">
              <span className="text-[12.5px] text-ink-body">
                {href ? (
                  <>
                    <a href={href} target="_blank" rel="noopener noreferrer" className="font-medium">
                      Open @{bot} in Telegram
                    </a>{' '}
                    and press Start, or send it:
                  </>
                ) : (
                  'Send the bot:'
                )}
              </span>
              <CopyField value={`/start ${telegram.code}`} label="the start command" />
              <span className={SMALL}>
                Waiting for the bot to hear it{telegram.code_expires_at ? `; the code works until ${new Date(telegram.code_expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}.
              </span>
            </div>
          ) : (
            <div>
              <Button size="sm" variant="primary" onClick={() => void code()}>
                Link Telegram
              </Button>
            </div>
          )}
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void save({ chat_id: chat.trim(), on: true }, 'Linked by chat id.');
            }}
          >
            <label htmlFor="telegram-chat" className="text-[12px] text-ink-muted">
              or paste a chat id
            </label>
            <span className="w-[180px] phone:w-full">
              <Input id="telegram-chat" inputMode="numeric" placeholder="123456789" value={chat} onChange={(e) => setChat(e.target.value)} />
            </span>
            {chat.trim() && (
              <Button type="submit" size="sm">
                Save
              </Button>
            )}
          </form>
        </div>
      )}
      <LastError error={telegram.error} sent={telegram.sent_at} />
      <Status outcome={outcome} />
    </Channel>
  );
}

function Channels({ ws, channels, onChanged, reload }: { ws: number; channels: AlertChannels; onChanged: (c: AlertChannels) => void; reload: () => void }) {
  return (
    <>
      <h3 className={H3}>Your channels</h3>
      <p className={SUB}>Yours alone, the same in every workspace you own. Turn on any of them; a channel that fails does not stop the others.</p>
      <ul className="m-0 list-none rounded-lg border border-hairline px-3.5 py-0" aria-label="Your channels">
        <PushChannel ws={ws} channels={channels} onChanged={onChanged} />
        <EmailChannel ws={ws} email={channels.email} onChanged={onChanged} />
        <TelegramChannel ws={ws} telegram={channels.telegram} onChanged={onChanged} reload={reload} />
      </ul>
    </>
  );
}

// ------------------------------------------------------------------ incidents

const NOTICE: Record<string, string> = { sent: 'told', silent: 'recorded only', pending: 'sending' };

function Incident({ i }: { i: AlertIncident }) {
  const open = i.state === 'open';
  return (
    <li className="grid gap-1 border-t border-hairline py-2.5 first:border-t-0" data-incident={i.id}>
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <Badge tone={open ? 'danger' : 'success'}>{open ? 'Open' : 'Closed'}</Badge>
        <a href={i.path} className="min-w-0 font-medium text-ink-display [overflow-wrap:anywhere]">
          {i.title}
        </a>
      </span>
      <span className={SMALL}>
        {i.kind === 'runner_offline' ? 'Runner offline' : 'CI failing'} · opened {ago(i.opened_at)} ({NOTICE[i.open_notice] ?? i.open_notice})
        {i.failures && i.failures > 1 ? ` · ${i.failures} failed runs` : ''}
        {i.closed_at ? ` · closed ${ago(i.closed_at)} (${NOTICE[i.close_notice ?? ''] ?? i.close_notice})` : ` · last seen ${ago(i.last_seen_at)}`}
      </span>
    </li>
  );
}

function Incidents({ ws }: { ws: number }) {
  const [list] = useLoad(() => api.GET('/api/workspaces/{ws_id}/alerts/incidents', { params: { path: { ws_id: ws } } }), `${ws}`);
  return (
    <>
      <h3 className={H3}>Recent incidents</h3>
      <p className={SUB}>Open first. “Recorded only” were already open when alerting was switched on, or were history when first seen.</p>
      {list.state === 'loading' && <p className="text-ink-muted">Loading…</p>}
      {list.state === 'error' && <Status outcome={{ ok: false, text: list.message }} />}
      {list.state === 'ok' && list.data.incidents.length === 0 && <p className="m-0 text-[12.5px] text-ink-muted">None yet.</p>}
      {list.state === 'ok' && list.data.incidents.length > 0 && (
        <ul className="m-0 list-none rounded-lg border border-hairline px-3.5 py-0" aria-label="Recent incidents">
          {list.data.incidents.map((i) => (
            <Incident key={i.id} i={i} />
          ))}
        </ul>
      )}
    </>
  );
}

// ------------------------------------------------------------------ the section

export function AlertsSection({ ws }: { ws: number }) {
  const [loaded, reload] = useLoad<AlertState>(() => api.GET('/api/workspaces/{ws_id}/alerts', { params: { path: { ws_id: ws } } }), `${ws}`);
  const [state, setState] = useState<AlertState | null>(null);
  useEffect(() => {
    if (loaded.state === 'ok') setState(loaded.data);
  }, [loaded]);
  return (
    <section id="alerts" aria-labelledby="h-alerts" className={SECTION}>
      <h2 id="h-alerts" className={H2}>
        Alerts
      </h2>
      <p className={SUB}>
        Shipyard tells this workspace’s owners once when a problem starts and once when it is over, never in between. Channel keys live
        on the server in <span className={mono}>board.env</span>; they are never shown here.
      </p>
      {!state && loaded.state === 'loading' && <p className="text-ink-muted">Loading…</p>}
      {!state && loaded.state === 'error' && <Status outcome={{ ok: false, text: loaded.message }} />}
      {state && (
        <>
          <Kinds ws={ws} settings={state.settings} onSaved={(settings) => setState({ ...state, settings })} />
          <Channels ws={ws} channels={state.channels} onChanged={(channels) => setState({ ...state, channels })} reload={reload} />
          <Incidents ws={ws} />
        </>
      )}
    </section>
  );
}
